<template>
  <div
    class="draggable-file-uploader d-flex align-center justify-center text-center flex-column"
    @click="triggerFileInputClick"
    @dragover="dragover"
    @dragleave="dragleave"
    @drop="drop"
  >
    <div>
      <template v-if="!file || errorMessage">
        <v-icon size="40" icon="$uploadFile" />
        <div class="subtitle2" v-html="description" />
        <div class="body2 text-indigo-medium">{{ subtitle }}</div>
        <div
          v-if="!!errorMessage"
          class="body2 text-red-dark mt-2 d-flex align-center justify-center"
        >
          <v-icon icon="icon-warning-xs" color="red-faint" size="24" class="mr-2" />
          {{ errorMessage }}
        </div>
      </template>
      <template v-else>
        <v-icon class="no-hex-icon" size="70" icon="$dataNoHexagon" />
        <div class="subtitle2 mt-2">{{ fileName }}</div>
        <div class="text-indigo-medium body3">{{ fileSize }}</div>
        <div class="body2 d-flex justify-center mt-4">
          <div class="text-green-dark mr-2">
            {{ $t("draggableFileUploader.fileCompatible") }}
          </div>
          <div class="coro-link" @click="removeFile($event)">
            {{ $t("general.remove") }}
          </div>
        </div>
      </template>
      <v-file-input
        ref="fileInput"
        v-model="file"
        :accept="accept"
        class="d-none"
        @update:modelValue="handleUpload"
      />
    </div>
  </div>
</template>
<script lang="ts">
import { computed, defineComponent, ref } from "vue";
import { i18n } from "@/plugins/i18n";
import { convertSize } from "@/_helpers/utils.ts";

export default defineComponent({
  props: {
    description: {
      type: String,
      default() {
        return i18n.global.t("draggableFileUploader.description");
      },
    },
    subtitle: {
      type: String,
      default() {
        return i18n.global.t("draggableFileUploader.subtitle");
      },
    },
    accept: {
      type: String,
      default() {
        return "text/csv";
      },
    },
    errorMessage: {
      type: String,
      default: "",
    },
  },
  emits: ["file-changed"],
  setup(props, { emit }) {
    const file = ref<File | File[]>();
    const fileInput = ref();
    const handleUpload = (event: File | File[]) => {
      file.value = event;
      emit("file-changed", (file.value as File[])[0] || file.value);
    };
    const dragover = (event: DragEvent) => {
      event.preventDefault();
      (event.currentTarget as HTMLDivElement)?.classList.add("draggable-file-uploader--dragover");
    };
    const dragleave = (event: DragEvent) => {
      (event.currentTarget as HTMLDivElement)?.classList.remove(
        "draggable-file-uploader--dragover"
      );
    };
    const drop = (event: DragEvent) => {
      event.preventDefault();
      (event.currentTarget as HTMLDivElement)?.classList.remove(
        "draggable-file-uploader--dragover"
      );
      const dropFile = event?.dataTransfer?.files[0];
      if (dropFile?.type && !props.accept.includes(dropFile.type)) {
        file.value = undefined;
        return;
      }
      if (dropFile) {
        file.value = [dropFile];
        emit("file-changed", file.value[0]);
      }
    };
    const removeFile = (event: MouseEvent) => {
      event.stopPropagation();
      file.value = undefined;
      emit("file-changed", file.value);
    };
    const triggerFileInputClick = () => {
      fileInput.value.click();
    };

    const fileName = computed(() => (file.value as File[])[0]?.name || (file.value as File).name);
    const fileSize = computed(() =>
      (file.value as File[])[0]?.size
        ? convertSize((file.value as File[])[0]?.size, 1)
        : convertSize((file.value as File).size, 1)
    );

    return {
      file,
      fileInput,
      handleUpload,
      dragover,
      dragleave,
      drop,
      fileName,
      removeFile,
      fileSize,
      triggerFileInputClick,
    };
  },
});
</script>
<style scoped lang="scss">
.draggable-file-uploader {
  width: 100%;
  height: 270px;
  border-radius: 8px;
  border: dashed 1px rgb(var(--v-theme-primary));
  transition: 0.3s;
}

.draggable-file-uploader--dragover {
  background-color: rgb(var(--v-theme-indigo-pale));
}

.draggable-file-uploader:hover {
  background-color: rgb(var(--v-theme-indigo-pale));
  cursor: pointer;
}
.no-hex-icon {
  background-color: rgb(var(--v-theme-indigo-faint));
  border-radius: 8px;
}
</style>
